import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaPlay,
  FaFileAlt,
  FaLock,
  FaCheckDouble,
  FaUsers,
  FaTimes,
  FaExclamationCircle,
} from "react-icons/fa";
import "./EntitlementScreen.css";
import { useNavigate } from "react-router-dom";

import MonthSelect from "./MonthSelect";
import YearSelect from "./YearSelect";
import GetCommodities from "../../Dropdowns/getCommoditiesAll";
import GetAllDistricts from "../../Dropdowns/getAllDistricts";
import GetSchemes from "../../Dropdowns/getSchemes";
import GenEntitlementTable from "./GenEntitlementTable";

const MONTH_NAMES = [
  "",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const getMonthName = (m) => MONTH_NAMES[parseInt(m)] || m;

const getActionLabel = (serviceType) => {
  if (!serviceType) return "Run";
  const st = serviceType.toLowerCase();
  if (st.includes("pull")) return "Pull";
  if (st.includes("apply")) return "Apply";
  if (st.includes("generation")) return "Generate";
  if (st.includes("generate")) return "Generate";
  if (st.includes("start")) return "Start";
  return "Run";
};

const EntitlementPage = () => {
  const navigate = useNavigate();

  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [keyRegisterMonth, setKeyRegisterMonth] = useState("");
  const [keyRegisterYear, setKeyRegisterYear] = useState("");

  const [policySchemes, setPolicySchemes] = useState([]);
  const [policyCommodities, setPolicyCommodities] = useState([]);

  const [processes, setProcesses] = useState([]);

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const [showTable, setShowTable] = useState(false);
  const [error, setError] = useState(null);

  const [generatedEntitlements, setGeneratedEntitlements] = useState(
    () => new Set(),
  );

  const [executionMessage, setExecutionMessage] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentService, setCurrentService] = useState(null);
  const [modalFormData, setModalFormData] = useState({
    distCode: "",
    schemeId: "",
    commId: "",
    startDate: "",
    endDate: "",
  });

  useEffect(() => {
    const now = new Date();
    setMonth((now.getMonth() + 1).toString());
    setYear(now.getFullYear().toString());
    setKeyRegisterMonth((now.getMonth() + 1).toString());
    setKeyRegisterYear(now.getFullYear().toString());
  }, []);

  useEffect(() => {
    if (
      executionMessage &&
      (executionMessage.type === "success" || executionMessage.type === "error")
    ) {
      const timer = setTimeout(() => {
        setExecutionMessage(null);
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [executionMessage]);

  const handleSearch = async (isRefresh = false) => {
    if (!month || !year || !keyRegisterMonth || !keyRegisterYear) {
      setError("Please select both Allocation Month/Year and KeyRegister Month/Year.");
      return;
    }

    if (!isRefresh) {
      setExecutionMessage(null);
      setLoading(true);
      setProcesses([]);
      setShowTable(false);
      setGeneratedEntitlements(new Set());
    }

    setError(null);

    try {
      // Fetch Process Status
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/api/entitlement/status`,
        { month, year, keyRegisterMonth, keyRegisterYear },
      );
      setProcesses(response.data || []);

      const genResponse = await axios.post(
        `${process.env.REACT_APP_URL}/api/entitlement/generated-status`,
        { month, year, keyRegisterMonth, keyRegisterYear },
      );
      setGeneratedEntitlements(new Set(genResponse.data || []));

      const policyRes = await axios.post(
        `${process.env.REACT_APP_URL}/api/entitlement/policy-masters`,
        { month, year }
      );
      
      if (policyRes.data) {
        if (policyRes.data.schemes) {
           setPolicySchemes(policyRes.data.schemes.map(s => ({ value: String(s.value), label: s.label })));
        }
        if (policyRes.data.commodities) {
           setPolicyCommodities(policyRes.data.commodities.map(c => ({ value: String(c.value), label: c.label })));
        }
      }

      setShowTable(true);
    } catch (err) {
      console.error("Search error:", err);
      if (!isRefresh) {
        setError("Failed to load processes. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (serviceType) => {
    setCurrentService(serviceType);
    setModalFormData({
      distCode: "",
      schemeId: "",
      commId: "",
      startDate: "",
      endDate: "",
    });

    setIsModalOpen(true);
  };

  // Check if the combination is already generated
  const isCombinationAlreadyGenerated = () => {
    if (currentService !== "Generate Entitlement") return false;

    const dist = modalFormData.distCode || "ALL";
    const scheme = String(modalFormData.schemeId || "0");
    const comm = String(modalFormData.commId || "0");

    if (dist === "ALL" && scheme === "0" && comm === "0") return false;

    if (dist !== "ALL") {
      let schemeDone = false;
      let commDone = false;

      if (
        scheme !== "0" &&
        generatedEntitlements.has(`${dist}|scheme|${scheme}`)
      ) {
        schemeDone = true;
      }
      if (comm !== "0" && generatedEntitlements.has(`${dist}|comm|${comm}`)) {
        commDone = true;
      }

      if (scheme !== "0" && comm === "0" && schemeDone) return true;
      if (comm !== "0" && scheme === "0" && commDone) return true;
      if (scheme !== "0" && comm !== "0" && schemeDone && commDone) return true;
    }

    return false;
  };

  const isDuplicateSelection = isCombinationAlreadyGenerated();

  const handleRunProcess = async () => {
    setIsModalOpen(false);
    const serviceType = currentService;

    setActionLoading(serviceType);
    setError(null);
    setExecutionMessage(null);

    const payload = {
      month,
      year,
      keyRegisterMonth,
      keyRegisterYear,
      serviceType,
    };

    if (serviceType === "Generate Entitlement") {
      payload.distCode =
        modalFormData.distCode.trim() === "" ? "ALL" : modalFormData.distCode;
      payload.schemeId =
        modalFormData.schemeId === "" ? 0 : parseInt(modalFormData.schemeId);
      payload.commId =
        modalFormData.commId === "" ? 0 : parseInt(modalFormData.commId);
    } else if (serviceType === "Start Distribution") {
      if (!modalFormData.startDate || !modalFormData.endDate) {
        setExecutionMessage({
          type: "error",
          msg: "Please select Start and End Dates.",
        });
        setActionLoading(null);
        return;
      }
      payload.startDate = modalFormData.startDate;
      payload.endDate = modalFormData.endDate;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_URL}/api/entitlement/execute`,
        payload,
      );

      setExecutionMessage({
        type: "success",
        msg: "Process completed successfully!",
      });

      await handleSearch(true);
    } catch (err) {
      console.error("Execution error:", err);
      let msg = "Error executing process.";
      if (err.response && err.response.data && err.response.data.message) {
        msg = err.response.data.message;
      } else if (err.message) {
        msg = err.message;
      }
      setExecutionMessage({
        type: "error",
        msg: msg,
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirm = async (serviceType) => {
    if (
      !window.confirm(
        "Are you sure you verified the reports? This will confirm the data.",
      )
    )
      return;

    setActionLoading(serviceType);
    setExecutionMessage(null);

    try {
      await axios.post(`${process.env.REACT_APP_URL}/api/entitlement/confirm`, {
        month,
        year,
        keyRegisterMonth,
        keyRegisterYear,
        serviceType,
      });
      setExecutionMessage({
        type: "success",
        msg: "Data Confirmed Successfully!",
      });

      await handleSearch(true);
    } catch (error) {
      console.error("Confirm error", error);
      setExecutionMessage({ type: "error", msg: "Confirmation Failed." });
    } finally {
      setActionLoading(null);
    }
  };

  const goToReport = (path) => {
    navigate(path, {
      state: { selectedMonth: month, selectedYear: year },
    });
  };

  const pullProcess = processes.find(
    (p) => p.serviceType === "Change Statement Pull",
  );
  const isPullConfirmed =
    pullProcess?.status === "Y" && pullProcess?.confirmedTime;
  const isPullCompleted = pullProcess?.status === "Y";

  return (
    <div className="container">
      <div className="tableheader py-2">ENTITLEMENT GENERATION FORM</div>

      <div className="form-body">
        <div className="form-group">
          <label>Key Register Month:</label>
          <MonthSelect
            value={keyRegisterMonth}
            onChange={(e) => setKeyRegisterMonth(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Key Register Year:</label>
          <YearSelect
            value={keyRegisterYear}
            onChange={(e) => setKeyRegisterYear(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Allocation Month:</label>
          <MonthSelect
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Year:</label>
          <YearSelect value={year} onChange={(e) => setYear(e.target.value)} />
        </div>

        <button
          className="button button2"
          onClick={() => handleSearch(false)}
          disabled={loading || actionLoading || !month || !year || !keyRegisterMonth || !keyRegisterYear}
          style={{ opacity: loading ? 0.7 : 1 }}
        >
          {loading ? "Searching..." : "Search"}
        </button>
      </div>

      {loading && !showTable && (
        <p className="msg-loading">Loading process...</p>
      )}

      {error && <p className="msg-error">{error}</p>}

      {showTable && (
        <div
          id="container"
          className="table-section"
          style={{
            opacity: actionLoading ? 0.6 : 1,
            transition: "opacity 0.3s",
          }}
        >
          <h1 className="tableheader py-2">
            Entitlement Screen for the Month of {getMonthName(month)}, {year}
          </h1>

          <table style={{ width: "100%" }}>
            <thead>
              <tr className="tableheader">
                <th>SNO</th>
                <th>STATUS</th>
                <th>TITLE</th>
                <th>ACTION</th>
                <th>UPDATE TIME</th>
                <th>CONFIRMATION</th>
              </tr>
            </thead>
            <tbody>
              {processes.map((proc, index) => {
                let isRunDisabled = loading || actionLoading;
                let disableReason = "";

                const isCurrentRowLoading = actionLoading === proc.serviceType;

                const pullProcess = processes.find((p)=>p.serviceType === "Change Statement Pull");
                const isPullConfirmed = pullProcess?.status === "Y" && pullProcess?.confirmedTime;

                const applyProcess = processes.find((p)=>p.serviceType === "Change Statement Apply");
                const isApplyDone = applyProcess?.status === "Y";

                if (proc.serviceType === "Change Statement Apply") {
                  if (!isPullConfirmed) {
                    isRunDisabled = true;
                    disableReason = "Pull must be Confirmed first";
                  }
                }

                if(proc.serviceType === "Generate Entitlement") {
                  if(!isApplyDone) {
                    isRunDisabled = true;
                    disableReason = "Apply must be Done first";
                  }
                }

                return (
                  <tr key={proc.serviceType}>
                    <td className="text-center">{index + 1}</td>
                    <td className="text-center">
                      {proc.status === "Y" ? (
                        <FaCheckCircle color="green" size={22} />
                      ) : (
                        <FaTimesCircle color="red" size={22} />
                      )}
                    </td>
                    <td className="text-left">
                      <strong>{proc.title}</strong>
                    </td>
                    <td className="text-center">
                      {proc.status !== "Y" ? (
                        <button
                          className="btn-run"
                          onClick={() => handleOpenModal(proc.serviceType)}
                          disabled={isRunDisabled}
                          title={disableReason}
                        >
                          {isCurrentRowLoading ? (
                            <span>
                              {getActionLabel(proc.serviceType)}ing...
                            </span>
                          ) : (
                            <>
                              <FaPlay className="icon" />{" "}
                              {getActionLabel(proc.serviceType)}
                            </>
                          )}
                        </button>
                      ) : (
                        <span className="label-done">Done</span>
                      )}
                    </td>
                    <td className="text-center update-time">
                      {proc.updatedTime
                        ? proc.updatedTime.substring(
                            0,
                            proc.updatedTime.lastIndexOf(":"),
                          )
                        : "---"}
                    </td>
                    <td className="text-center">
                      {proc.serviceType === "Change Statement Pull" &&
                        proc.status === "Y" &&
                        !proc.confirmedTime && (
                          <button
                            className="button button2"
                            onClick={() => handleConfirm(proc.serviceType)}
                            disabled={isRunDisabled}
                            style={{ minWidth: "100px" }}
                          >
                            {actionLoading === proc.serviceType ? (
                              "Locking..."
                            ) : (
                              <>
                                <FaCheckDouble /> Confirm
                              </>
                            )}
                          </button>
                        )}

                      {proc.confirmedTime && (
                        <div className="confirmed-badge">
                          <FaLock /> Confirmed <br />
                          <span className="time">
                            {proc.confirmedTime.substring(
                              0,
                              proc.confirmedTime.lastIndexOf(":"),
                            )}
                          </span>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}

              {processes.length === 0 && !loading && (
                <tr>
                  <td colSpan="6" className="text-center">
                    No processes found for this month.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {executionMessage && (
            <div
              className={`exec-toast ${
                executionMessage.type === "error"
                  ? "exec-toast--error"
                  : "exec-toast--success"
              }`}
            >
              {executionMessage.msg}
            </div>
          )}
        </div>
      )}

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <span>{currentService}</span>
              <button
                className="close-btn"
                onClick={() => setIsModalOpen(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group-modal form-full-width">
                  <label>Target Period</label>
                  <input
                    type="text"
                    value={`${getMonthName(month)} - ${year}`}
                    disabled
                    className="form-input read-only-field"
                  />
                </div>

                {currentService === "Generate Entitlement" && (
                  <>
                    <div className="form-group-modal">
                      <label>District</label>
                      <GetAllDistricts
                        onLoadingChange={() => {}}
                        onChange={(val) =>
                          setModalFormData((prev) => ({
                            ...prev,
                            distCode: val,
                          }))
                        }
                      />
                    </div>
                    <div className="form-group-modal">
                      <label>Scheme</label>
                      <select
                        className="w-24"
                        value={modalFormData.schemeId}
                        onChange={(e) => setModalFormData((prev) => ({ ...prev, schemeId: e.target.value }))}
                      >
                        <option value="0">--All--</option>
                        {policySchemes.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group-modal">
                      <label>Commodity</label>
                      <select
                        className="w-24"
                        value={modalFormData.commId}
                        onChange={(e) => setModalFormData((prev) => ({ ...prev, commId: e.target.value }))}
                      >
                        <option value="0">--All--</option>
                        {policyCommodities.map((c) => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {currentService === "Start Distribution" && (
                  <div
                    className="form-full-width"
                    style={{ display: "flex", gap: "20px" }}
                  >
                    <div className="form-group-modal" style={{ flex: 1 }}>
                      <label>Distribution Start Date</label>
                      <input
                        type="date"
                        className="form-input"
                        value={modalFormData.startDate}
                        onChange={(e) => {
                          const newStartDate = e.target.value;
                          setModalFormData((prev) => {
                            let newEndDate = prev.endDate;
                            if (newEndDate && newStartDate > newEndDate) {
                              newEndDate = "";
                            }
                            return {
                              ...prev,
                              startDate: newStartDate,
                              endDate: newEndDate,
                            };
                          });
                        }}
                        required
                      />
                    </div>

                    <div className="form-group-modal" style={{ flex: 1 }}>
                      <label>Distribution End Date</label>
                      <input
                        type="date"
                        className="form-input"
                        value={modalFormData.endDate}
                        onChange={(e) =>
                          setModalFormData({
                            ...modalFormData,
                            endDate: e.target.value,
                          })
                        }
                        min={modalFormData.startDate}
                        disabled={!modalFormData.startDate}
                        required
                      />
                    </div>
                  </div>
                )}
              </div>
              {isDuplicateSelection && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginTop: "12px",
                    padding: "10px 15px",
                    backgroundColor: "#fff5f5",
                    border: "1px solid #feb2b2",
                    borderRadius: "10px",
                  }}
                >
                  <FaExclamationCircle color="red" />
                  <span style={{ color: "#c53030", fontSize: "0.9rem" }}>
                    This specific combination of District, Scheme, and Commodity
                    is already generated. You cannot run it again.
                  </span>
                </div>
              )}
              {currentService === "CB_OB Generation" && (
                <div className="info-box" style={{ marginTop: "20px" }}>
                  <strong>Automatic Calculation</strong>
                  <br />
                  The system will automatically calculate the Opening Balance
                  for <b>{getMonthName(month)}</b> based on the Closing Balance
                  of the previous month.
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </button>
              {/* <button className="btn-confirm" onClick={handleRunProcess}>
                {getActionLabel(currentService)}
              </button> */}
              <button
                className="btn-confirm"
                onClick={handleRunProcess}
                disabled={isDuplicateSelection}
                style={{
                  opacity: isDuplicateSelection ? 0.5 : 1,
                  cursor: isDuplicateSelection ? "not-allowed" : "pointer",
                }}
              >
                <FaPlay size={12} /> {getActionLabel(currentService)}
              </button>
            </div>
          </div>
        </div>
      )}

      {processes.length > 0 && isPullCompleted && !loading && (
        <div className="reports-card">
          <div className="reports-card-header">
            Reports for {getMonthName(month)} - {year}
          </div>
          <div className="report-buttons">
            <button
              className="button button2"
              onClick={() => goToReport("/RcmsChangeStatementReport")}
            >
              <FaFileAlt /> RCMS Change Statement
            </button>
            <button
              className="button button2"
              onClick={() => goToReport("/RCMSmemberchangestatement")}
            >
              <FaUsers /> RCMS Members Detail
            </button>
          </div>
          <div className="reports-card-footer">
            {isPullCompleted
              ? "Data has been confirmed successfully."
              : '*You can verify these reports before clicking "Confirm".'}
          </div>
        </div>
      )}

      {showTable && (
        <div style={{ marginTop: "30px", marginBottom: "30px" }}>
          {generatedEntitlements.size > 0 ? (
            <GenEntitlementTable
            generatedEntitlements={generatedEntitlements}
              policySchemes={policySchemes}
              policyCommodities={policyCommodities}
            />
          ) : (
            ""
          )}
        </div>
      )}
    </div>
  );
};

export default EntitlementPage;
